import type { FC, ReactNode } from 'react';
import { PlatformNavStrip, type PlatformNavExtra } from '../PlatformNavStrip';

export type { PlatformNavExtra };

export type PlatformShellProps = {
  onNavigate: (view: string) => void;
  backView: string;
  backLabel?: string;
  title?: string;
  extras?: PlatformNavExtra[];
  /** Anchor id for skip-link target (must be unique per view) */
  mainId: string;
  children: ReactNode;
  /** Extra classes on `<main>` (e.g. background gradients) */
  mainClassName?: string;
  ariaLabel?: string;
  /** Passed through to `PlatformNavStrip` (e.g. extra buttons on the right) */
  navChildren?: ReactNode;
};

/**
 * Consistent platform chrome: sticky `PlatformNavStrip` + skip link + landmark `<main>`.
 * Uses app `onNavigate` (not router `Link`) to match `App.tsx` view routing.
 */
export const PlatformShell: FC<PlatformShellProps> = ({
  onNavigate,
  backView,
  backLabel = 'Back',
  title,
  extras = [],
  mainId,
  children,
  mainClassName = '',
  ariaLabel,
  navChildren,
}) => {
  const label = ariaLabel ?? (title ? `${title} — main content` : 'Main content');

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <PlatformNavStrip
        onNavigate={onNavigate}
        backView={backView}
        backLabel={backLabel}
        title={title}
        extras={extras}
        mainId={mainId}
      >
        {navChildren}
      </PlatformNavStrip>
      <main
        id={mainId}
        tabIndex={-1}
        aria-label={label}
        className={`min-h-screen scroll-mt-20 outline-none focus-visible:ring-2 focus-visible:ring-amber-400/90 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 ${mainClassName}`}
      >
        {children}
      </main>
    </div>
  );
};
